/* ============================================================
   CALDAVIA — motion layer (behaviour only; all visuals live in
   caldavia-motion.css).

   Runs only when the head bootstrap set `html.cald-js`, which it
   skips in the theme editor and for prefers-reduced-motion. Every
   hidden state in the CSS is gated on that class, so if this file
   fails to load the page simply renders static.

   1. Hero headline — split into words for a masked rise.
   2. Scroll reveal — section headings (line mask), text, category
      links, buttons, product cards and images that start BELOW the
      fold. Anything already on screen at load is left alone. Once
      an element has arrived its reveal classes are removed, so the
      theme's own hover / state styles take over again.
   3. Scroll state — `html.cald-scrolled` for the header, plus a
      light hero parallax on desktop.
   4. Cart feedback — the cart icon pulses when its count changes.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('cald-js')) return;

  var main = document.getElementById('MainContent');
  if (!main) return;

  /* ---------- helpers ---------- */
  function wrapInMask(el, maskClass) {
    var mask = document.createElement('span');
    mask.className = maskClass;
    var inner = document.createElement('span');
    while (el.firstChild) inner.appendChild(el.firstChild);
    mask.appendChild(inner);
    el.appendChild(mask);
    return inner;
  }

  function isPlainText(el) {
    return el.children.length === 0 && el.textContent.trim().length > 0;
  }

  /* ---------- 1. Hero headline word split ---------- */
  function splitHeroHeadline() {
    var block = document.querySelector('.hero .text-block');
    if (!block || block.classList.contains('cald-split')) return;

    var target = block.querySelector('h1, h2, h3, p') || block;
    if (!isPlainText(target)) return; // never touch rich markup

    var words = target.textContent.trim().split(/\s+/);
    if (!words.length || words.length > 14) return;

    var frag = document.createDocumentFragment();
    words.forEach(function (word, i) {
      var mask = document.createElement('span');
      mask.className = 'cald-w';
      var inner = document.createElement('span');
      inner.textContent = word;
      inner.style.setProperty('--i', i);
      mask.appendChild(inner);
      frag.appendChild(mask);
      if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
    });
    target.textContent = '';
    target.appendChild(frag);
    block.classList.add('cald-split');
  }

  try { splitHeroHeadline(); } catch (e) { /* static headline is fine */ }

  /* ---------- 2. Scroll reveal ---------- */
  function setupReveal() {
    if (!('IntersectionObserver' in window)) return;

    var vh = window.innerHeight || root.clientHeight;
    if (!vh) return; // hidden / prerendered tab: never hide content blind

    function settle(el) {
      // After the entrance, hand the element back to the theme's styles.
      var delay = parseInt(el.style.getPropertyValue('--cald-d'), 10) || 0;
      setTimeout(function () {
        el.classList.remove('cald-r', 'cald-in');
        el.style.removeProperty('--cald-d');
      }, 1400 + delay);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('cald-in');
        observer.unobserve(entry.target);
        settle(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    // Returns true when the element will be revealed (it starts below the fold).
    function mark(el, type, delay) {
      if (el.hasAttribute('data-cald')) return false;
      el.setAttribute('data-cald', type);
      if (el.getBoundingClientRect().top < vh * 0.92) return false;
      el.classList.add('cald-r');
      if (delay) el.style.setProperty('--cald-d', delay + 'ms');
      observer.observe(el);
      return true;
    }

    function insideCard(el) {
      return !!el.closest('product-card, .product-grid__item');
    }

    function claimed(el) {
      return (el.parentElement && el.parentElement.closest('[data-cald]')) || el.querySelector('[data-cald]');
    }

    var sections = main.querySelectorAll(':scope > .shopify-section');
    sections.forEach(function (section, index) {
      if (index === 0) return; // the first section (hero) has its own entrance
      var step = 0;

      // Category links ("shop by room") rise one after another.
      section.querySelectorAll('.collection-links__link').forEach(function (link, i) {
        mark(link, 'up', i * 90);
      });

      // Section headings: a line rising out of a mask.
      section.querySelectorAll('h1, h2, h3').forEach(function (h) {
        if (insideCard(h) || claimed(h) || !isPlainText(h)) return;
        if (mark(h, 'line', step * 90)) wrapInMask(h, 'cald-line');
        step++;
      });

      // Remaining text blocks: a soft fade-up, staggered after the heading.
      section.querySelectorAll('.text-block').forEach(function (el) {
        if (insideCard(el) || claimed(el)) return;
        mark(el, 'up', step * 90);
        step++;
      });

      // Section-level buttons.
      section.querySelectorAll('.button, .button-secondary, .button-custom').forEach(function (el) {
        if (insideCard(el) || claimed(el)) return;
        mark(el, 'up', step * 90 + 60);
      });

      // Product cards: staggered by position within their row.
      section.querySelectorAll('product-card').forEach(function (card, i) {
        mark(card, 'card', (i % 4) * 85);
      });

      // Editorial imagery outside cards: masked reveal.
      section.querySelectorAll('img').forEach(function (img) {
        if (insideCard(img) || claimed(img)) return;
        mark(img, 'img', 0);
      });
    });

    // Footer columns rise in one after another.
    document.querySelectorAll('footer .footer-content > *').forEach(function (el, i) {
      mark(el, 'up', i * 90);
    });
  }

  try { setupReveal(); } catch (e) { /* reveal is decorative */ }

  /* ---------- 3. Scroll state + hero parallax ---------- */
  var hero = document.querySelector('.hero');
  var parallax = hero && window.matchMedia('(min-width: 750px)').matches;
  // Horizon can scroll inside .page-wrapper instead of the window.
  var scroller = document.querySelector('.page-wrapper');
  var ticking = false;

  function scrollY() {
    var y = window.scrollY || window.pageYOffset || 0;
    if (scroller && scroller.scrollTop > y) y = scroller.scrollTop;
    return y;
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      var y = scrollY();
      root.classList.toggle('cald-scrolled', y > 8);
      if (parallax && y < 1400) hero.style.setProperty('--cald-hero-y', y.toFixed(0));
    });
  }

  // Capture phase hears scrolls from the window and from nested scrollers alike.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  onScroll();

  /* ---------- 4. Cart feedback ---------- */
  function watchCart() {
    var icon = document.querySelector('cart-icon');
    if (!icon || !('MutationObserver' in window)) return;

    var last = icon.textContent.replace(/\s+/g, '');
    var timer;
    new MutationObserver(function () {
      var now = icon.textContent.replace(/\s+/g, '');
      if (now === last) return;
      last = now;
      icon.classList.remove('cald-bump');
      void icon.offsetWidth; // restart the animation
      icon.classList.add('cald-bump');
      clearTimeout(timer);
      timer = setTimeout(function () { icon.classList.remove('cald-bump'); }, 900);
    }).observe(icon, { childList: true, subtree: true, characterData: true });
  }

  try { watchCart(); } catch (e) { /* feedback is decorative */ }
})();
